/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import { getItemBanksList } from '../services/getItemBank';
import { getPufv2ItemBanksList } from '../services/getPufv2ItemBank';
import { getAssociatedItemBank } from '../services/getAssociatedItemBank';
import { addItemBank } from '../services/addItemBank';
import ItemBank from './ItemBank';
import Constants from '../constants';

const { LEARNOSITY, PUFV2, DEFAULT_LEARNOSITY_TEXT } = Constants;

const isPufV2Assessment = (projectAssessment, projectAssessmentType) =>
  projectAssessmentType === PUFV2 || projectAssessment?.isPufV2Title === true;

export default types
  .model('ItemBanks', {
    selectedItemBank: '',
    selectedItemBankName: '',
    selectedAssessmentType: '',
    itemBanks: types.array(ItemBank),
    pufv2ItemBanks: types.array(ItemBank)
  })
  .actions(self => ({
    updateSelectedItemBank(itemBankId, itemBankName) {
      self.selectedItemBank = itemBankId;
      self.selectedItemBankName = itemBankName;
    },
    updateSelectedAssessmentType(assessmentType) {
      self.selectedAssessmentType = assessmentType;
    },
    /**
     * @function - Fetching Item Banks  data using getItemBanks service
     */
    async getItemBanks() {
      const items = await getItemBanksList();
      this.setItemBanks(items);
    },

    async getPufv2ItemBanks(lob) {
      const items = await getPufv2ItemBanksList(lob);
      this.setPufv2ItemBanks(items);
    },

    async getProjectAssociatedItemBankService(entityUrn) {
      const associatedItemBank = await getAssociatedItemBank(entityUrn);
      return associatedItemBank[0];
    },

    async hydrateProjectItemBank(
      entityUrn,
      projectAssessment,
      projectAssessmentType
    ) {
      const isPufV2 = isPufV2Assessment(
        projectAssessment,
        projectAssessmentType
      );

      if (!isPufV2) {
        try {
          const associatedItemBank = await getAssociatedItemBank(entityUrn);
          const itemBank = associatedItemBank?.[0];
          if (itemBank?.ItemBankId) {
            self.updateSelectedItemBank(
              itemBank.ItemBankId,
              itemBank.ItemBankName
            );
            self.updateSelectedAssessmentType(LEARNOSITY);
            return;
          }
        } catch (error) {
          // Learnosity association not found; fall back to project metadata.
        }
      }

      if (projectAssessment?.ItemBankId) {
        const assessmentType = isPufV2
          ? PUFV2
          : projectAssessmentType || LEARNOSITY;
        self.updateSelectedItemBank(
          projectAssessment.ItemBankId,
          projectAssessment.ItemBankName
        );
        self.updateSelectedAssessmentType(assessmentType);
        return;
      }

      if (projectAssessmentType === DEFAULT_LEARNOSITY_TEXT) {
        self.updateSelectedItemBank('', '');
        self.updateSelectedAssessmentType(DEFAULT_LEARNOSITY_TEXT);
        return;
      }

      self.updateSelectedItemBank('', '');
      self.updateSelectedAssessmentType('');
    },

    async addItemBankToProject(payload) {
      return addItemBank(payload);
    },

    /**
     * @function - Saving all the ItemBank data in self object
     * @param {items}
     */
    setItemBanks(items) {
      items.forEach(item => {
        self.itemBanks.push(item);
      });
    },

    setPufv2ItemBanks(items) {
      self.pufv2ItemBanks.clear();
      items.forEach(item => {
        if (item.ItemBankId) {
          self.pufv2ItemBanks.push(item);
        }
      });
    }
  }));
